import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Rule, RuleSet } from 'angular2-query-builder';
import { NgxSpinnerService } from 'ngx-spinner';
import { Audience, AudienceParameters } from 'src/app/models/services/audience';
import { AudienceQueryCountRequest } from 'src/app/models/services/audienceQueryCount';
import { CreateAudienceRequest } from 'src/app/models/services/createAudience';
import { LookAlike } from 'src/app/models/services/lookAlike';
import { LookAlikeSourceTypes } from 'src/app/models/services/lookAlikeSourceTypes.enum';
import { LookAlikeParameter, LookAlikeParametersGroup, LookAlikeParametersTaxonomy, MaxParameterIndexAllowed } from 'src/app/models/services/lookAlikeTaxonomy';
import { AudiencesService } from 'src/app/services/audiences/audiences.service';
import { DialogService } from 'src/app/services/core/dialog.service';
import { InputNameDialogService } from 'src/app/services/core/input-name-dialog.service';
import { AudienceParametersDialogService } from 'src/app/services/dialogs/audience-parameters-dialog.service';
import { GlobalsService } from 'src/app/services/globals.service';
import { LookAlikesService } from 'src/app/services/look-alikes.service';
import { ThemeOptions } from 'src/app/theme-options';

@Component({
  selector: 'audience-lookalikes-block',
  templateUrl: './audience-lookalikes-block.component.html',
  styleUrls: ['./audience-lookalikes-block.component.sass']
})
export class AudienceLookalikesBlockComponent implements OnInit {
  // Flatten definition
  private flatten = (x) => x.reduce((a, b) => a.concat(Array.isArray(b) ? this.flatten(b) : b), []);

  @Input()
  public audience: Audience;

  @Output() 
  onLookAlikeCreated = new EventEmitter<Audience>();

  public showList: boolean = true;

  public sourceType: LookAlikeSourceTypes = LookAlikeSourceTypes.Audience;
  public sourceIdentifier: string;
  public navigationUrlBase: string;
  public lookAlikes: Array<LookAlike>;
  // NEW LOOK ALIKE
  sourceMatchedRecords: number;
  lookAlikeParametersTaxonomiesUnfiltered: Array<LookAlikeParametersTaxonomy>;
  lookAlikeParametersTaxonomiesGlobalFiltered: Array<LookAlikeParametersTaxonomy>;
  lookAlikeParametersTaxonomies: Array<LookAlikeParametersTaxonomy>;
  resultsCountData = null;
  minimumPercentageValue: number = 40;
  minimumAffinityIndex: number = 150;
  totalProfiles: number = 0;
  selectedCondition: string = 'and';
  maxParametersIndexesAllowed: MaxParameterIndexAllowed[] = [];
  categoryNameVisible: string;
  currentSelection: RuleSet;
  currentReadableSelection: string[];

  constructor(
    private globals: ThemeOptions,
    private lookAlikesServices: LookAlikesService,
    private audiencesServices: AudiencesService,
    private dialogService: DialogService,
    private audienceParametersDialogService: AudienceParametersDialogService,
    private inputNameDialogService: InputNameDialogService,
    private spinnerService: NgxSpinnerService,
    private globalsService: GlobalsService,
    private router: Router) { }

  ngOnInit(): void {
    // If no active company navigate to root
    if (!this.globalsService.getActiveCompany()) {
      this.router.navigate(["/"]);
      return;
    }
    this.spinnerService.show();     
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.sourceIdentifier = this.audience.id.toString();
    this.sourceMatchedRecords = this.audience.profilesCount; 
    if (this.showList) {
      if (!this.lookAlikes)
        this.lookAlikesServices.getLookAlikes(LookAlikeSourceTypes.Audience, this.audience.id.toString()).subscribe(
          (data: Array<LookAlike>) => {
            this.spinnerService.hide();
            this.lookAlikes = data;
          },
          () => {
            this.spinnerService.hide();
          }      
        );      
    } else {
      if (!this.lookAlikeParametersTaxonomiesUnfiltered)
        this.lookAlikesServices.getLookAlikesParameters(this.sourceType, this.sourceIdentifier).subscribe(
          (data: Array<LookAlikeParametersTaxonomy>) => {
            this.spinnerService.hide();
            this.lookAlikeParametersTaxonomiesUnfiltered = data;        
            this.refreshFilteredTaxonomies();
          },
          () => {
            this.spinnerService.hide();
          }      
        );
    }
  }

  ngOnDestroy(): void {
    this.globals.backEnabled = false;
    this.globals.backLabel = "";
  }

  getStatusStyle = (index: number): string => {
        return "pe-7s-check";
  }

  getStatusLabel = (index: number): string => {
        return "Done";
  }

  createAudience = (index: number): void => {
    let lookAlike: LookAlike = this.lookAlikes[index];
    this.audienceParametersDialogService.show(
      (audienceParameters: AudienceParameters) => {
        // Save audience
        this.spinnerService.show();
        let request: CreateAudienceRequest = {
          name: audienceParameters.name,
          categoryId: audienceParameters.categoryId,
          query: lookAlike.conditions,
          lookAlikeSourceType: this.sourceType,
          lookAlikeSourceIdentifier: this.sourceIdentifier
        };
        this.audiencesServices.createAudience(request).subscribe(
          () => {
            this.spinnerService.hide();
            this.dialogService.showMessage("Create audience", "Audience created");
          },
          () => {
            this.spinnerService.hide();
          }
        );
      },
      () => { }
    );  
  }

  deleteLookAlike = (index: number): void => {
    let data: LookAlike = this.lookAlikes[index];
    this.dialogService.showConfirmation(
      `Are you sure to remove "${data.name}"?`,
      () => {        
        this.lookAlikesServices.removeLookAlike(this.sourceType, data).subscribe(
          () => {
            this.lookAlikes.splice(index, 1);
          });        
      },
      () => { });
  }  

  downloadLookAlikeParametersCsv = (): void => {
    this.spinnerService.show();
    this.lookAlikesServices.downloadLookAlikesParametersCsv(this.sourceType,this.sourceIdentifier).subscribe(
      (result: Blob) => {
        /*
        if (window.navigator && window.navigator.msSaveOrOpenBlob) { // for IE
          window.navigator.msSaveOrOpenBlob(csvData, file_name);
        } else { // for Non-IE (chrome, firefox etc.)*/
        let fileName: string = `parameters ${ this.sourceIdentifier }.csv`;
        var a = document.createElement("a");
        document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = URL.createObjectURL(result);
        a.download = fileName;

        this.spinnerService.hide();

        a.click();
        URL.revokeObjectURL(a.href)
        a.remove();
      },
      () => {
        this.spinnerService.hide();
      });
  }  

  backToList = (): void => {
    this.showList = true;
    this.ngOnChanges(null);
  }

  // NEW LOOK ALIKE
  newLookAlike = (): void => {
    this.showList = false;
    this.ngOnChanges(null);
  }

  refreshCount = (): void => {
    var conditions = this.currentSelection;
    if (!conditions || conditions.rules.length == 0) {
      this.dialogService.showMessage('Lookalike', 'You must select at least one condition');
      return;
    }
      
    this.spinnerService.show();
    let request: AudienceQueryCountRequest = {
      lookAlikeSourceType: this.sourceType,
      lookAlikeSourceIdentifier: this.sourceIdentifier,
      query: conditions
    }; 
    this.audiencesServices.getAudienceCounts(request).subscribe((data) => {
      this.totalProfiles = data.totalCount;
      this.resultsCountData = {
        name: "Total",
        color: "#93f291",
        children: [
          {
            value: this.sourceMatchedRecords,
            color: "#34a832"
          },
          {
            value: data.audienceCount - this.sourceMatchedRecords,
            color: "#93f291"
          }
        ]
      };
      this.spinnerService.hide();
    });
  }

  createLookAlike = (): void => {
    this.inputNameDialogService.show(
      "Create look alike",
      "Are you sure you want to create a look alike?",
      (name: string) => {
        // Save audience
        this.spinnerService.show();
        let lookAlike: LookAlike = {
            sourceType: this.sourceType,
            sourceIdentifier: this.sourceIdentifier,
            name: name,
            conditions: this.currentSelection
        }

        this.lookAlikesServices.createLookAlike(this.sourceType, lookAlike).subscribe(
          () => {
            this.spinnerService.hide();
            this.dialogService.showMessage("Create look alike", "Look alike created");
            this.onLookAlikeCreated.emit(this.audience);
            this.lookAlikes = null;
            this.backToList();
          },
          () => {
            this.spinnerService.hide();
          }
        );
      },
      () => { }
    );
  }  

  refreshCurrentSelection = (): void => {
    var parameterGroups: LookAlikeParametersGroup[] = this.flatten(this.lookAlikeParametersTaxonomies.map(x => x.parametersGroups));
    var selection: LookAlikeParameter[] = this.flatten(parameterGroups.map(y => y.parameters.filter(z => z.selected)));
    this.currentSelection = {
      condition: this.selectedCondition,
      rules: selection.map((x: LookAlikeParameter): Rule => this.getRuleFromLookAlikeParameter(x))
    };
    this.currentReadableSelection = selection.map((x: LookAlikeParameter): string => {
      var parameterTaxonomy = this.lookAlikeParametersTaxonomies.find(pt => pt.parametersGroups.findIndex(pg => pg.parameters.findIndex(p => p.fieldName == x.fieldName && p.fieldValue == x.fieldValue ) >= 0) >= 0);
      var parameterGroup = parameterTaxonomy.parametersGroups.find(pg => pg.parameters.findIndex(p => p.fieldName == x.fieldName && p.fieldValue == x.fieldValue ) >= 0);

      if (x.fieldName.startsWith("fieldSegment") || x.fieldName.startsWith("fieldCluster"))
        return `${parameterTaxonomy.name} \\ ${parameterGroup.name} is ${x.fieldDescription} affinity is higher or equal than 10`;
      
        return `${parameterTaxonomy.name} \\ ${parameterGroup.name} is ${x.fieldDescription}`;
    });
  }

  getRuleFromLookAlikeParameter = (parameter: LookAlikeParameter): Rule => {
    if (parameter.fieldName.startsWith("fieldSegment") || parameter.fieldName.startsWith("fieldCluster")) {
        return {
          field: parameter.fieldName,
          operator: ">=",
          value: "10"
        };
    } else {
        return {
          field: parameter.fieldName,
          operator: "=",
          value: parameter.fieldValue.toString()
        };

    }
  }

  refreshFilteredTaxonomies = (): void => {
    this.lookAlikeParametersTaxonomiesGlobalFiltered = this.lookAlikeParametersTaxonomiesUnfiltered.map((x): LookAlikeParametersTaxonomy => {
      return {
        name: x.name,
        parametersGroups: x.parametersGroups.map((y): LookAlikeParametersGroup => {
          return {
            name: y.name,
            parameters: y.parameters.filter((z): boolean => {
              return z.representationPercentage >= this.minimumPercentageValue &&
              z.affinityIndex >= this.minimumAffinityIndex;
            }).sort((x, y) => x.affinityIndex < y.affinityIndex ? 1 : x.affinityIndex > y.affinityIndex ? -1 : 0) as Array<LookAlikeParameter>
          };
        }).filter((y): boolean => {
          return y.parameters.length > 0;
        }) as (Array<LookAlikeParametersGroup>)
      };
    }).filter((x): boolean => {
      return x.parametersGroups.length > 0;
    }) as Array<LookAlikeParametersTaxonomy>;

    this.lookAlikeParametersTaxonomies = 
      this.lookAlikeParametersTaxonomiesGlobalFiltered.map((x): LookAlikeParametersTaxonomy => {
        return {
          name: x.name,
          parametersGroups: x.parametersGroups.map((y): LookAlikeParametersGroup => {
            return {
              name: y.name,
              parameters: y.parameters.filter((z, index): boolean => {
                var maxParameterIndexAllowedStored = this.maxParametersIndexesAllowed.find(maxParam => maxParam.parameterGroupName == y.name);
                var maxParameterIndexAllowed = !!maxParameterIndexAllowedStored ? maxParameterIndexAllowedStored.value : 4;
                return index <= maxParameterIndexAllowed;
              }) as Array<LookAlikeParameter>
            };
          }).filter((y): boolean => {
            return y.parameters.length > 0;
          }) as (Array<LookAlikeParametersGroup>)
        };
      }).filter((x): boolean => {
        return x.parametersGroups.length > 0;
      }) as Array<LookAlikeParametersTaxonomy>;
  }

  getSourceTypeDescription = (): string => {
    switch (this.sourceType) {
      case LookAlikeSourceTypes.Segment:
        return "segment";
        case LookAlikeSourceTypes.Audience:
          return "audience";        
    }
  }

  showMoreClicked = (parametersGroup: LookAlikeParametersGroup): void => {
    var maxParameterIndexAllowedStored = this.maxParametersIndexesAllowed.find(x => x.parameterGroupName == parametersGroup.name);
    if (!!maxParameterIndexAllowedStored)
      maxParameterIndexAllowedStored.value += 5;
    else
      this.maxParametersIndexesAllowed.push({
        parameterGroupName: parametersGroup.name,
        value: 9
      });

    this.refreshFilteredTaxonomies();
  }

  showLessClicked = (parametersGroup: LookAlikeParametersGroup): void => {
    var maxParameterIndexAllowedStored = this.maxParametersIndexesAllowed.find(x => x.parameterGroupName == parametersGroup.name);
    if (!!maxParameterIndexAllowedStored && maxParameterIndexAllowedStored.value > 5) {
      maxParameterIndexAllowedStored.value -= 5;
      this.refreshFilteredTaxonomies();
    }
  }

  showMoreVisible = (parametersGroup: LookAlikeParametersGroup): boolean => {
    var originalParameters = 
      this.lookAlikeParametersTaxonomiesGlobalFiltered
        .find(x => x.name == this.categoryNameVisible)
        .parametersGroups
          .find(x => x.name == parametersGroup.name)
          .parameters;          
    var maxParameterIndexAllowedStored = this.maxParametersIndexesAllowed.find(x => x.parameterGroupName == parametersGroup.name);
    var currentMaxIndex = !!maxParameterIndexAllowedStored ? maxParameterIndexAllowedStored.value : 4;

    return currentMaxIndex < originalParameters.length - 1;
  }

  showLessVisible = (parametersGroup: LookAlikeParametersGroup): boolean => {
    var maxParameterIndexAllowedStored = this.maxParametersIndexesAllowed.find(x => x.parameterGroupName == parametersGroup.name);
    var currentMaxIndex = !!maxParameterIndexAllowedStored ?maxParameterIndexAllowedStored.value : 4;

    return currentMaxIndex > 4;
  }

  toggleCategoryVisibility  = (category: LookAlikeParametersTaxonomy): void => {
    this.categoryNameVisible = (this.categoryNameVisible != category.name) ? category.name : undefined;
  }  

  onAttributeCheckedChange = (): void => {    
    this.refreshCurrentSelection();
    this.resultsCountData = null;
  }

}
